import jsPDF from "jspdf";

interface EmissionBreakdown {
  [key: string]: number;
}

interface PDFExportData {
  title: string;
  totalEmissions: number;
  breakdown: EmissionBreakdown;
  scope1?: number;
  scope2?: number;
  calculationType: "personal" | "business" | "lifestyle";
  date?: string;
}

export function exportToPDF(data: PDFExportData) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Header
  doc.setFillColor(46, 139, 87); // #2E8B57
  doc.rect(0, 0, pageWidth, 40, "F");
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.text("Greenhouse Gas Calculator", pageWidth / 2, 20, { align: "center" });
  
  doc.setFontSize(14);
  doc.setFont("helvetica", "normal");
  doc.text(data.title, pageWidth / 2, 30, { align: "center" });
  
  // Date
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(10);
  const reportDate = data.date || new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.text(`Report Generated: ${reportDate}`, pageWidth / 2, 50, { align: "center" });
  
  // Total Emissions Box
  doc.setFillColor(240, 253, 244);
  doc.setDrawColor(46, 139, 87);
  doc.setLineWidth(0.5);
  doc.roundedRect(20, 60, pageWidth - 40, 30, 3, 3, "FD");
  
  doc.setTextColor(46, 139, 87);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Total Annual Emissions", pageWidth / 2, 72, { align: "center" });
  
  doc.setFontSize(24);
  doc.text(`${data.totalEmissions.toFixed(2)} tons CO₂e`, pageWidth / 2, 84, { align: "center" });
  
  // Business-specific Scope breakdown
  if (data.calculationType === "business" && data.scope1 !== undefined && data.scope2 !== undefined) {
    let yPos = 100;
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Scope Breakdown", 20, yPos);
    
    yPos += 10;
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    
    // Scope 1
    doc.setFillColor(255, 237, 213);
    doc.roundedRect(20, yPos, (pageWidth - 50) / 2, 30, 2, 2, "F");
    doc.setTextColor(234, 88, 12);
    doc.setFont("helvetica", "bold");
    doc.text("Scope 1 Emissions", 25, yPos + 8);
    doc.setFontSize(18);
    doc.text(`${data.scope1.toFixed(2)} tons`, 25, yPos + 22);
    
    // Scope 2
    doc.setFillColor(254, 249, 195);
    doc.roundedRect(pageWidth / 2 + 5, yPos, (pageWidth - 50) / 2, 30, 2, 2, "F");
    doc.setTextColor(202, 138, 4);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Scope 2 Emissions", pageWidth / 2 + 10, yPos + 8);
    doc.setFontSize(18);
    doc.text(`${data.scope2.toFixed(2)} tons`, pageWidth / 2 + 10, yPos + 22);
    
    yPos += 40;
  }
  
  // Emissions Breakdown
  let yPos = data.calculationType === "business" ? 150 : 100;
  
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Detailed Breakdown", 20, yPos);
  
  yPos += 10;
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  
  const breakdownEntries = Object.entries(data.breakdown);
  breakdownEntries.forEach(([category, value]) => {
    const percentage = ((value / data.totalEmissions) * 100).toFixed(1);
    const capitalizedCategory = category.charAt(0).toUpperCase() + category.slice(1);
    
    doc.setTextColor(60, 60, 60);
    doc.text(capitalizedCategory, 25, yPos);
    
    doc.setTextColor(46, 139, 87);
    doc.setFont("helvetica", "bold");
    doc.text(`${value.toFixed(2)} tons (${percentage}%)`, pageWidth - 25, yPos, { align: "right" });
    
    doc.setFont("helvetica", "normal");
    yPos += 8;
  });
  
  // Equivalencies Section
  yPos += 10;
  if (yPos > pageHeight - 80) {
    doc.addPage();
    yPos = 20;
  }
  
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.text("Your Impact in Context", 20, yPos);
  
  yPos += 10;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60, 60, 60);
  
  const equivalencies = [
    { label: "Trees needed to offset", value: Math.round(data.totalEmissions * 16).toLocaleString() },
    { label: "Miles driven (average car)", value: Math.round(data.totalEmissions * 2485).toLocaleString() },
    { label: "Smartphone charges", value: Math.round(data.totalEmissions * 121643).toLocaleString() },
    { label: "One-way flights NYC-LA", value: Math.round(data.totalEmissions * 0.556).toLocaleString() },
    { label: "Homes powered for 1 year", value: (data.totalEmissions * 0.091).toFixed(2) },
  ];
  
  equivalencies.forEach((eq) => {
    doc.text(`• ${eq.label}: ${eq.value}`, 25, yPos);
    yPos += 7;
  });
  
  // Recommendations Section
  yPos += 10;
  if (yPos > pageHeight - 60) {
    doc.addPage();
    yPos = 20;
  }
  
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.text("Reduction Recommendations", 20, yPos);
  
  yPos += 10;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60, 60, 60);
  
  const recommendations = [
    "Switch to renewable energy sources for your home",
    "Use public transportation or carpool when possible",
    "Consider electric or hybrid vehicles for your next purchase",
    "Reduce air travel and opt for video conferencing",
    "Improve home insulation to reduce heating/cooling needs",
    "Choose energy-efficient appliances and LED lighting",
  ];
  
  recommendations.forEach((rec) => {
    doc.text(`• ${rec}`, 25, yPos);
    yPos += 7;
  });
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(
    "Generated by www.greenhousegascalculator.com",
    pageWidth / 2,
    pageHeight - 10,
    { align: "center" }
  );
  
  // Save PDF
  const filename = `GHG-Inventory-${data.calculationType}-${new Date().getTime()}.pdf`;
  doc.save(filename);
}
